import { useCallback, useEffect, useState } from 'react';
import {
  Card, Group, Text, Badge, Stack, SegmentedControl,
  Box, Code, Collapse, ActionIcon, Tooltip, Progress, Skeleton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconLoader2, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useArchbaseRemoteServiceApi } from '@archbase/data';
import { RouteXRouteJobService } from '@services/RouteJobService';
import { API_TYPE } from '@ioc/IOCTypes';
import type { RouteJobDto } from '@domain/routeJob/RouteJobDto';

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  SOLVED: { color: 'teal', label: 'Resolvido' },
  SOLVING: { color: 'blue', label: 'Processando' },
  PENDING: { color: 'yellow', label: 'Pendente' },
  FAILED: { color: 'red', label: 'Falhou' },
  CANCELLED: { color: 'gray', label: 'Cancelado' },
};

interface JobStats {
  totalDistance?: number;
  vehiclesUsed?: number;
  visitsAssigned?: number;
  visitsUnassigned?: number;
}

function extractStats(job: RouteJobDto): JobStats | undefined {
  const output = job.outputData as { statistics?: JobStats } | undefined;
  return output?.statistics;
}

function extractMode(job: RouteJobDto): string | undefined {
  const output = job.outputData as { options?: { solverMode?: string } } | undefined;
  return output?.options?.solverMode;
}

function extractRoutesCount(job: RouteJobDto): number | undefined {
  const output = job.outputData as { routes?: unknown[] } | undefined;
  return output?.routes?.length;
}

export default function RouteJobsView() {
  const [jobs, setJobs] = useState<RouteJobDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [expanded, setExpanded] = useState<string | null>(null);

  const service = useArchbaseRemoteServiceApi<RouteXRouteJobService>(API_TYPE.RouteXRouteJob);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const page = await service.findAll(0, 100);
      setJobs(page?.content ?? []);
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e?.message ?? 'Falha ao carregar jobs', color: 'red' });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const filtered = filter === 'ALL' ? jobs : jobs.filter((j) => j.status === filter);

  const formatTime = (ms?: number) => {
    if (!ms || ms === 0) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleString('pt-BR') : '-');

  const counts = {
    ALL: jobs.length,
    SOLVED: jobs.filter((j) => j.status === 'SOLVED').length,
    SOLVING: jobs.filter((j) => j.status === 'SOLVING').length,
    PENDING: jobs.filter((j) => j.status === 'PENDING').length,
    FAILED: jobs.filter((j) => j.status === 'FAILED').length,
  };

  return (
    <Box p="xl" style={{ height: '100%' }}>
      <Stack gap="xl">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text size="xl" fw={700} style={{ letterSpacing: '-0.02em' }}>Jobs de Roteirização</Text>
            <Text size="sm" c="dimmed">Acompanhar execuções de otimização</Text>
          </Stack>
          <Tooltip label="Atualizar">
            <ActionIcon variant="subtle" onClick={fetchJobs} loading={loading}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <SegmentedControl
          value={filter}
          onChange={setFilter}
          data={[
            { label: `Todos (${counts.ALL})`, value: 'ALL' },
            { label: `Resolvidos (${counts.SOLVED})`, value: 'SOLVED' },
            { label: `Processando (${counts.SOLVING})`, value: 'SOLVING' },
            { label: `Pendentes (${counts.PENDING})`, value: 'PENDING' },
            { label: `Falhos (${counts.FAILED})`, value: 'FAILED' },
          ]}
        />

        {loading ? (
          <Stack gap="sm">
            {[...Array(4)].map((_, i) => <Skeleton key={i} height={72} radius="sm" />)}
          </Stack>
        ) : filtered.length === 0 ? (
          <Card padding="xl" radius="sm" withBorder>
            <Text c="dimmed" ta="center">Nenhum job encontrado</Text>
          </Card>
        ) : (
          <Stack gap="sm">
            {filtered.map((job) => {
              const cfg = STATUS_CONFIG[job.status] ?? { color: 'gray', label: job.status };
              const isExpanded = expanded === job.id;
              const visitsTotal = job.visitsCount ?? 0;
              const stats = extractStats(job);
              const assigned = stats?.visitsAssigned ?? 0;
              const unassigned = stats?.visitsUnassigned ?? 0;
              const successRate = visitsTotal > 0 ? Math.round((assigned / visitsTotal) * 100) : 0;
              const mode = extractMode(job);
              const routesCount = extractRoutesCount(job);

              return (
                <Card
                  key={job.id}
                  padding="md"
                  radius="sm"
                  withBorder
                  style={{ cursor: 'pointer', borderLeft: `3px solid var(--mantine-color-${cfg.color}-6)` }}
                  onClick={() => setExpanded(isExpanded ? null : job.id)}
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      <ActionIcon variant="subtle" size="sm">
                        {isExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                      </ActionIcon>
                      <Stack gap={0}>
                        <Group gap="xs">
                          <Text size="sm" fw={700} ff="monospace">{job.id}</Text>
                          <Badge
                            size="xs"
                            variant="light"
                            color={cfg.color}
                            leftSection={job.status === 'SOLVING' ? <IconLoader2 size={10} style={{ animation: 'spin 1s linear infinite' }} /> : undefined}
                          >
                            {cfg.label}
                          </Badge>
                          {mode && <Badge size="xs" variant="outline" color="gray">{mode}</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed">{job.clientId} — {formatDate(job.createdAt)}</Text>
                      </Stack>
                    </Group>
                    <Group gap="xl">
                      <Stack gap={0} align="center">
                        <Text size="xs" c="dimmed">Veículos</Text>
                        <Text size="sm" fw={600} ff="monospace">{job.vehiclesCount ?? '-'}</Text>
                      </Stack>
                      <Stack gap={0} align="center">
                        <Text size="xs" c="dimmed">Visitas</Text>
                        <Text size="sm" fw={600} ff="monospace">{stats ? `${assigned}/${visitsTotal}` : visitsTotal}</Text>
                      </Stack>
                      <Stack gap={0} align="center">
                        <Text size="xs" c="dimmed">Distância</Text>
                        <Text size="sm" fw={600} ff="monospace">{stats?.totalDistance ? `${stats.totalDistance}km` : '-'}</Text>
                      </Stack>
                      <Stack gap={0} align="center">
                        <Text size="xs" c="dimmed">Tempo</Text>
                        <Text size="sm" fw={600} ff="monospace">{formatTime(job.solvingTimeMs)}</Text>
                      </Stack>
                    </Group>
                  </Group>

                  <Collapse expanded={isExpanded}>
                    <Box mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                      <Group gap="xl" mb="sm">
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">Score</Text>
                          <Code>{job.score ?? '-'}</Code>
                        </Stack>
                        {routesCount !== undefined && (
                          <Stack gap={2}>
                            <Text size="xs" c="dimmed">Rotas geradas</Text>
                            <Text size="sm" fw={600}>{routesCount}</Text>
                          </Stack>
                        )}
                        {stats && visitsTotal > 0 && (
                          <Stack gap={2}>
                            <Text size="xs" c="dimmed">Taxa alocação</Text>
                            <Group gap="xs">
                              <Progress
                                value={successRate}
                                size="sm"
                                style={{ width: 80 }}
                                color={successRate === 100 ? 'teal' : successRate > 90 ? 'blue' : 'orange'}
                              />
                              <Text size="xs" fw={500}>{successRate}%</Text>
                            </Group>
                          </Stack>
                        )}
                      </Group>
                      {unassigned > 0 && (
                        <Badge size="sm" variant="light" color="orange">{unassigned} visitas não alocadas</Badge>
                      )}
                      {job.status === 'FAILED' && job.errorMessage && (
                        <Text size="xs" c="red" mt="xs">{job.errorMessage}</Text>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
